import { ChangeDetectionStrategy, Component, DestroyRef, OnChanges, SimpleChanges, computed, inject, input, model, output, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';

import { ComissaoAtividadeSalvarRequest, ComissaoSituacaoAtividadeResponse } from '@/core/api/sicom-api/comissoes/atividades/comissao-atividade.model';
import { ComissaoAtividadeService } from '@/core/api/sicom-api/comissoes/atividades/comissao-atividade.service';
import { CanDirective } from '@/core/permissions/can.directive';
import { PERMISSOES } from '@/core/permissions/permissoes.catalog';
import { AtividadeAta, ReuniaoComissao } from '@/features/comissoes/dashboard-comissoes/comissoes-dashboard.model';

import { DialogoPadraoDirective } from '@/shared/components/layout/dialogo-padrao/dialogo-padrao.directive';
/** Responsável selecionável na atividade (nome + id/uf). */
export interface ResponsavelAtividadeAta {
    label: string;
    id: number;
    uf: string;
}

/** Diálogo de cadastro/edição de atividade da ata: form tipado, próximo código via serviço e emissão do request. */
@Component({
    selector: 'app-ata-dialog-atividade',
    standalone: true,
    imports: [DialogoPadraoDirective, ReactiveFormsModule, ButtonModule, DialogModule, InputTextModule, SelectModule, CanDirective],
    templateUrl: './ata-dialog-atividade.html',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class AtaDialogAtividadeComponent implements OnChanges {
    private readonly formBuilder = inject(FormBuilder);
    private readonly comissaoAtividadeService = inject(ComissaoAtividadeService);
    private readonly destroyRef = inject(DestroyRef);

    /** Visibilidade do diálogo (two-way). */
    readonly visivel = model(false);
    /** Situações possíveis da atividade. */
    readonly situacoes = input<ComissaoSituacaoAtividadeResponse[]>([]);
    /** Responsáveis selecionáveis (membros + convidados). */
    readonly responsaveis = input<ResponsavelAtividadeAta[]>([]);
    /** Reunião de contexto (estrutura/código para o request). */
    readonly reuniao = input<ReuniaoComissao | null>(null);
    /** Atividade em edição (null = nova). */
    readonly atividadeEdicao = input<AtividadeAta | null>(null);
    /** Emite o request pronto (com o id da tarefa quando edição). */
    readonly salvar = output<{ idTarefa?: number; request: ComissaoAtividadeSalvarRequest }>();

    protected readonly roleAtividadesEditar = PERMISSOES.comissoes.atividades.editar;
    protected readonly titulo = computed(() => (this.atividadeEdicao() ? 'Editar Atividade' : 'Nova Atividade'));
    protected readonly proximoCodigoAtividade = signal('');

    protected readonly formulario = this.formBuilder.nonNullable.group({
        idTarefa: [null as number | null],
        idResponsavel: [null as number | null],
        descricao: [''],
        observacao: [''],
        dataInicio: [''],
        dataFim: [''],
        idSituacaoTarefa: [null as number | null]
    });

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['visivel'] && this.visivel()) {
            this.semearFormulario();
        }
    }

    protected fechar(): void {
        this.visivel.set(false);
    }

    protected aoSalvar(): void {
        const formVal = this.formulario.getRawValue();
        if (!formVal.descricao.trim() || !formVal.idResponsavel || !formVal.dataInicio) {
            return;
        }

        const responsavel = this.responsaveis().find((r) => r.id === formVal.idResponsavel);
        if (!responsavel) {
            return;
        }

        const request: ComissaoAtividadeSalvarRequest = {
            idEstruturaOrganizacional: this.reuniao()?.idEstruturaOrganizacional,
            idSolicitanteResponsavel: responsavel.id,
            sgUfSolicitanteResponsavel: responsavel.uf,
            dsTarefa: formVal.descricao.trim(),
            txObservacao: formVal.observacao,
            dtInicio: formVal.dataInicio ? formVal.dataInicio + 'T00:00:00' : undefined,
            dtTermino: formVal.dataFim ? formVal.dataFim + 'T23:59:59' : undefined,
            idSituacaoTarefa: formVal.idSituacaoTarefa ?? this.situacaoPadrao() ?? undefined,
            idReuniao: this.reuniao() ? Number(this.reuniao()?.codigo) : undefined
        };

        this.salvar.emit({ idTarefa: formVal.idTarefa || undefined, request });
        this.fechar();
    }

    private semearFormulario(): void {
        const atividade = this.atividadeEdicao();
        this.proximoCodigoAtividade.set('');

        if (atividade) {
            this.formulario.setValue(
                {
                    idTarefa: atividade.idTarefa,
                    idResponsavel: atividade.idSolicitanteResponsavel ?? null,
                    descricao: atividade.descricao || '',
                    observacao: atividade.txObservacao || '',
                    dataInicio: this.formatarDataInput(atividade.dtInicio),
                    dataFim: this.formatarDataInput(atividade.dtTermino),
                    idSituacaoTarefa: atividade.idSituacaoTarefa ?? this.situacaoPadrao()
                },
                { emitEvent: false }
            );
            return;
        }

        const hoje = new Date().toISOString().substring(0, 10);
        this.formulario.setValue(
            {
                idTarefa: null,
                idResponsavel: null,
                descricao: '',
                observacao: '',
                dataInicio: hoje,
                dataFim: hoje,
                idSituacaoTarefa: this.situacaoPadrao()
            },
            { emitEvent: false }
        );
        this.carregarProximoCodigo();
    }

    private carregarProximoCodigo(): void {
        this.comissaoAtividadeService
            .consultarProximoCodigoAtividade()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (response) => {
                    this.proximoCodigoAtividade.set(response.codigoTarefa || (response.idTarefa ? String(response.idTarefa).padStart(6, '0') : ''));
                },
                error: () => {
                    this.proximoCodigoAtividade.set('');
                }
            });
    }

    private situacaoPadrao(): number | null {
        return this.situacoes()[0]?.idSituacaoTarefa ?? 1;
    }

    private formatarDataInput(data?: string | Date | null): string {
        if (!data) {
            return '';
        }
        const dataObj = typeof data === 'string' ? new Date(data) : data;
        return isNaN(dataObj.getTime()) ? '' : dataObj.toISOString().substring(0, 10);
    }
}
